import { Link, useNavigate, useLocation } from "react-router-dom";
import { Linkedin, Instagram, Youtube, Mail } from "lucide-react";

// Social icons kept available for future use; currently only email shown.
void Linkedin; void Instagram; void Youtube;

const COL_MODULES = [
  "Booking Infrastructure",
  "Partnership Infrastructure",
  "Lead Infrastructure",
  "Communication Infrastructure",
  "Operations Infrastructure",
  "Monetization Infrastructure",
];

const COL_COMPANY = [
  { label: "What We Build", target: "modules" },
  { label: "Process", target: "process" },
  { label: "Packages", target: "packages" },
  { label: "Who We Work With", target: "about" },
  { label: "FAQ", target: "faq" },
];

const COL_LEGAL = [
  { label: "Privacy Policy", path: "/privacy-policy" },
  { label: "Terms of Service", path: "/terms-of-service" },
  { label: "Refund Policy", path: "/refund-policy" },
  { label: "Disclaimer", path: "/disclaimer" },
  { label: "Cookie Policy", path: "/cookie-policy" },
];

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();

  const goToSection = (target) => {
    if (location.pathname !== "/") navigate(`/#${target}`);
    else {
      const el = document.getElementById(target);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#03040a] text-slate-400 pt-20 pb-10 border-t border-white/[0.06] overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid-fine opacity-30" aria-hidden />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <Link to="/" data-testid="footer-logo-link" className="inline-flex items-center gap-2.5">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-300 to-blue-500 text-slate-950 font-black tracking-tighter text-lg">
                B
              </span>
              <span className="font-extrabold text-white tracking-tight text-lg">
                Brand-in-a-Box
              </span>
            </Link>
            <p className="mt-5 text-slate-500 text-sm leading-relaxed max-w-sm">
              We build the systems behind growth. Business infrastructure for organizations, creators, cultural brands, sports organizations, education brands, and service businesses.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a
                href="mailto:hello@bigeyedeassystems.com"
                data-testid="footer-social-email"
                aria-label="Email"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/10 hover:border-cyan-400/50 hover:bg-cyan-400/5 text-slate-300 hover:text-cyan-300 transition-all px-4 h-9 text-sm"
              >
                <Mail className="h-4 w-4" /> hello@bigeyedeassystems.com
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4 text-xs font-mono tracking-widest uppercase">What We Build</h4>
            <ul className="space-y-3">
              {COL_MODULES.map((s) => (
                <li key={s}>
                  <button
                    data-testid={`footer-service-${s.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => goToSection("modules")}
                    className="text-sm text-slate-500 hover:text-white transition-colors"
                  >
                    {s}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4 text-xs font-mono tracking-widest uppercase">Studio</h4>
            <ul className="space-y-3">
              {COL_COMPANY.map((c) => (
                <li key={c.label}>
                  <button
                    data-testid={`footer-link-${c.label.toLowerCase()}`}
                    onClick={() => goToSection(c.target)}
                    className="text-sm text-slate-500 hover:text-white transition-colors"
                  >
                    {c.label}
                  </button>
                </li>
              ))}
              <li>
                <Link data-testid="footer-link-contact" to="/contact" className="text-sm text-slate-500 hover:text-white transition-colors">
                  Contact
                </Link>
              </li>
              <li>
                <button
                  data-testid="footer-link-request-proposal"
                  onClick={() => goToSection("proposal")}
                  className="text-sm text-cyan-300 hover:text-cyan-200 transition-colors font-medium"
                >
                  Book a Strategy Call →
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4 text-xs font-mono tracking-widest uppercase">Legal</h4>
            <ul className="space-y-3">
              {COL_LEGAL.map((l) => (
                <li key={l.label}>
                  <Link
                    data-testid={`footer-legal-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    to={l.path}
                    className="text-sm text-slate-500 hover:text-white transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-6">
              <a
                href="mailto:hello@bigeyedeassystems.com"
                data-testid="footer-email-link"
                className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"
              >
                <Mail className="h-4 w-4" /> hello@bigeyedeassystems.com
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/[0.06] flex flex-col md:flex-row md:items-center justify-between gap-4">
          <p className="text-xs text-slate-600">
            © {new Date().getFullYear()} Brand-in-a-Box. All rights reserved.
          </p>
          <p className="text-xs text-slate-600 font-mono tracking-wide">
            Founder-architected · Fixed-price scope · Tenant-owned builds
          </p>
        </div>
      </div>
    </footer>
  );
}
